import { renderToStaticMarkup } from 'react-dom/server';
import WysiwygWrapper from 'src/components/WysiwygWrapper';

const renderCmsHtml = value => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
        return '';
    }

    return renderToStaticMarkup(
        <WysiwygWrapper data={value} regularLinks textComponent={({ children }) => <p>{children}</p>} />
    );
};

const SERVICE_AD_STORAGE = 'ad_storage';
const SERVICE_AD_USER_DATA = 'ad_user_data';
const SERVICE_AD_PERSONALIZATION = 'ad_personalization';
const SERVICE_ANALYTICS_STORAGE = 'analytics_storage';

const updateGtagConsent = analyticsAllowed => {
    if (typeof window === 'undefined') {
        return;
    }

    const consentState = analyticsAllowed ? 'granted' : 'denied';
    const consentPayload = {
        [SERVICE_ANALYTICS_STORAGE]: consentState,
        [SERVICE_AD_STORAGE]: 'denied',
        [SERVICE_AD_USER_DATA]: 'denied',
        [SERVICE_AD_PERSONALIZATION]: 'denied',
    };

    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', consentPayload);
        return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'consent_update',
        consent: consentPayload,
    });
};

const hasAnalyticsConsent = payload => {
    const accepted = payload?.acceptedCategories || payload?.cookie?.categories || payload?.categories;

    return Array.isArray(accepted) && accepted.includes('analytics');
};

const buildCookieConsentConfig = (cookieSettings = {}) => {
    const title = cookieSettings.heading || 'Cookies';
    const bodyHtml = renderCmsHtml(cookieSettings.text);
    const description =
        bodyHtml ||
        'We use necessary cookies to ensure the website functions properly and optional analytics cookies to improve the site. You can manage your preferences at any time. For more information about what data is saved with our cookies, see our privacy policy.';
    const lifespan = parseInt(cookieSettings.lifespan, 10) || 365;

    return {
        cookie: {
            name: 'berg-cc',
            expiresAfterDays: lifespan,
        },
        guiOptions: {
            consentModal: {
                layout: 'box',
                position: 'bottom left',
                equalWeightButtons: true,
                flipButtons: false,
            },
            preferencesModal: {
                layout: 'box',
                position: 'right',
                equalWeightButtons: true,
                flipButtons: false,
            },
        },
        categories: {
            necessary: {
                enabled: true,
                readOnly: true,
            },
            analytics: {
                enabled: false,
                autoClear: {
                    cookies: [{ name: /^_ga/ }, { name: /^_gid/ }, { name: /^_ga_/ }],
                },
            },
        },
        onFirstConsent: payload => {
            updateGtagConsent(hasAnalyticsConsent(payload));
        },
        onConsent: payload => {
            updateGtagConsent(hasAnalyticsConsent(payload));
        },
        onChange: payload => {
            updateGtagConsent(hasAnalyticsConsent(payload));
        },
        language: {
            default: 'en',
            translations: {
                en: {
                    consentModal: {
                        title,
                        description,
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject non-essential',
                        showPreferencesBtn: 'Manage preferences',
                    },
                    preferencesModal: {
                        title: 'Cookie preferences',
                        acceptAllBtn: 'Accept all',
                        acceptNecessaryBtn: 'Reject non-essential',
                        savePreferencesBtn: 'Save preferences',
                        closeIconLabel: 'Close',
                        sections: [
                            {
                                title: 'Necessary',
                                description:
                                    'These cookies are required for the proper functioning of the website, for example for user authentication.',
                                linkedCategory: 'necessary',
                                cookieTable: {
                                    caption: 'Necessary cookies',
                                    headers: {
                                        name: 'Name',
                                        description: 'Description',
                                        duration: 'Duration',
                                    },
                                    body: [
                                        {
                                            name: 'GuestCustomerId',
                                            description: 'Identifies a guest customer.',
                                            duration: 'Session',
                                        },
                                        {
                                            name: 'token',
                                            description: 'Authentication for logged-in users.',
                                            duration: '1 year',
                                        },
                                        {
                                            name: 'user',
                                            description: 'Stores user profile for login.',
                                            duration: 'Session',
                                        },
                                    ],
                                },
                            },
                            {
                                title: 'Analytics',
                                description:
                                    'Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user’s experience.',
                                linkedCategory: 'analytics',
                                cookieTable: {
                                    caption: 'Analytics cookies',
                                    headers: {
                                        name: 'Name',
                                        description: 'Description',
                                        duration: 'Duration',
                                    },
                                    body: [
                                        {
                                            name: '_ga',
                                            description: 'Google Analytics identifier.',
                                            duration: '1 year',
                                        },
                                        {
                                            name: '_ga_*',
                                            description: 'Google Analytics identifier.',
                                            duration: '1 year',
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                },
            },
        },
    };
};

export default buildCookieConsentConfig;
